import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, Transition } from '@headlessui/react';
import { motion } from 'framer-motion';
import { 
  GlobeAltIcon, 
  Bars3Icon, 
  UserCircleIcon, 
  MagnifyingGlassIcon,
  HomeIcon,
  SparklesIcon,
  UserGroupIcon,
  PresentationChartBarIcon,
  QuestionMarkCircleIcon,
  CurrencyDollarIcon,
  HeartIcon
} from '@heroicons/react/24/outline';
import AuthModal from './auth/AuthModal';
import UserMenu from './UserMenu';

const Header: React.FC = () => {
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authModalView, setAuthModalView] = useState<'login' | 'signup'>('login');
  const navigate = useNavigate();
  const location = useLocation();

  const openAuthModal = (view: 'login' | 'signup') => {
    setAuthModalView(view);
    setIsAuthModalOpen(true);
  };

  const navigationItems = [
    { path: '/stays', label: 'Stays', icon: <HomeIcon className="h-5 w-5" /> },
    { path: '/experiences', label: 'Experiences', icon: <SparklesIcon className="h-5 w-5" />, isNew: true },
    { path: '/manager', label: 'Dashboard', icon: <PresentationChartBarIcon className="h-5 w-5" /> },
    { path: '/refer', label: 'Referrals', icon: <UserGroupIcon className="h-5 w-5" /> },
    { path: '/pricing', label: 'Pricing', icon: <CurrencyDollarIcon className="h-5 w-5" /> },
    { path: '/faq', label: 'FAQ', icon: <QuestionMarkCircleIcon className="h-5 w-5" /> },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex justify-between items-center h-20">
          <Link to="/" className="flex items-center group">
            <motion.img 
              src="https://a0.muscache.com/airbnb/static/logotype_favicon-21cc8e6c6a2cca43f061d2dcabdf6e58.ico"
              alt="Airbnb"
              className="h-8 w-auto transition-transform duration-200 group-hover:scale-110"
              whileHover={{ rotate: 360 }}
              transition={{ duration: 0.5 }}
            />
            <span className="text-airbnb-red text-xl font-semibold ml-2">airbnb</span>
          </Link>
          
          <nav className="hidden md:flex items-center space-x-6">
            {navigationItems.map((item) => (
              <Link 
                key={item.path}
                to={item.path}
                className={`flex items-center px-3 py-2 text-sm font-medium rounded-full transition-colors duration-200
                  ${location.pathname === item.path 
                    ? 'text-airbnb-red bg-red-50' 
                    : 'text-gray-700 hover:bg-gray-50'
                  }`}
              >
                {item.icon}
                <span className="ml-2">{item.label}</span>
                {item.isNew && (
                  <span className="ml-1.5 text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full font-medium">
                    New
                  </span>
                )}
              </Link>
            ))}
          </nav>
          
          <div className="flex items-center gap-4">
            <Link 
              to="/become-a-host"
              className="hidden md:flex items-center px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-full transition-colors duration-200"
            >
              Become a Host
            </Link>
            
            <button 
              className="p-2 hover:bg-gray-50 rounded-full transition-colors duration-200"
              aria-label="Change language"
            >
              <GlobeAltIcon className="h-5 w-5 text-gray-700" />
            </button>

            <Menu as="div" className="relative">
              <Menu.Button className="flex items-center gap-2 p-2 border border-gray-300 rounded-full hover:shadow-md transition-all duration-200">
                <Bars3Icon className="h-5 w-5 text-gray-600" />
                <UserCircleIcon className="h-8 w-8 text-gray-600" />
              </Menu.Button>

              <Transition
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items className="absolute right-0 mt-2 w-56 origin-top-right rounded-xl bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none divide-y divide-gray-100">
                  <div className="py-1">
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={() => openAuthModal('signup')}
                          className={`${
                            active ? 'bg-gray-50' : ''
                          } flex w-full items-center px-4 py-2 text-sm text-gray-700`}
                        >
                          Sign up
                        </button>
                      )}
                    </Menu.Item>
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={() => openAuthModal('login')}
                          className={`${
                            active ? 'bg-gray-50' : ''
                          } flex w-full items-center px-4 py-2 text-sm text-gray-700`}
                        >
                          Log in
                        </button>
                      )}
                    </Menu.Item>
                  </div>
                </Menu.Items>
              </Transition>
            </Menu>
          </div>
        </div>
      </div>

      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        initialView={authModalView}
      />
    </header>
  );
};

export default Header;